import React from 'react';
import { Col, Row } from 'react-bootstrap';

import {
  EventPlaylist,
  EventPlaylistLocation,
  EventPlaylistFacilitator
} from '../types/event';
import { QRLink } from './QRLink';
import { ColView } from './col-view';
import { User } from '../types/user';
import { Country } from '../types/country';
import { Library } from '../types/library';
import { WSTActivity } from './activity-event/wst';
import { formatDateToDDMMYYYY } from '../utils/date';
import { EnvironmentList } from '../types/environment';
import { QRTaskActivity } from './activity-event/qr-task';
import { InstanceContext } from '../contexts/instance-provider';

export interface ActivityEventProps {
  displayUrl?: boolean;
  clientName?: string;
  event?: EventPlaylist;
  index?: number;
  eventId?: number;
  users?: User[];
  libraries?: Library[];
  environments?: EnvironmentList[];
  languages?: string[];
  countries?: Country[];
  facilitators?: EventPlaylistFacilitator[];
  locations?: EventPlaylistLocation[];
  onCopyGame?: (index: number) => void;
  onDeleteGame?: (index: number) => void;
  onChange?: (game: EventPlaylist, index: number) => void;
}

export type dictionary = {
  id: number;
  name: string;
};

export const ActivityEventView: React.FC<ActivityEventProps> = (props) => {
  const {
    index,
    locations,
    displayUrl,
    facilitators,
    event: eventActivity = {}
  } = props;

  const { tiloWebGlUrl } = React.useContext(InstanceContext);

  const gamelocations = React.useMemo(() => {
    if (!locations) return '';

    return locations.map((l) => `${l.country} ${l.city}`).join(', ');
  }, [locations]);

  const facilitatorData = React.useMemo(() => {
    if (!facilitators) return '';

    return facilitators.map((l) => l.fullName).join(', ');
  }, [facilitators]);

  const isTilo = eventActivity.type === 'TILO';
  const isTil = eventActivity.type === 'TIL';
  const isSpark = eventActivity.type === 'SPA' || !!eventActivity.sparkGameId;
  const isWst =
    eventActivity.type === 'WST' ||
    eventActivity.name === "World's Strongest Team" ||
    eventActivity.game === "World's Strongest Team";

  if (isWst) return <WSTActivity {...props} />;

  return (
    <>
      <div className={'mt-4'}>Activity {(index as number) + 1}</div>
      <div className={'card p-4'}>
        {!isTil && (
          <Row className={'p-2'}>
            <Col md={10}>
              <div className={'d-flex flex-wrap'}>
                {/* line 1 */}
                <ColView
                  className={'d-flex flex-column'}
                  colSpec={6}
                  labelClassName={'text-muted'}
                  label={'Activity Date + Time'}
                  value={`${formatDateToDDMMYYYY(
                    eventActivity?.startDateUtc!,
                    true
                  )} - ${formatDateToDDMMYYYY(eventActivity?.endDateUtc!, true)}`}
                />
                {!isTilo && (
                  <ColView
                    colSpec={6}
                    className={'d-flex flex-column'}
                    labelClassName={'text-muted'}
                    label={'Activity Code'}
                    value={`${eventActivity?.code}`}
                  />
                )}

                {/* line 2 */}
                {!isSpark && (
                  <ColView
                    colSpec={12}
                    className={'d-flex flex-column mt-2'}
                    labelClassName={'text-muted'}
                    label={'Environment'}
                    value={`${eventActivity?.environment}`}
                  />
                )}

                {/* line 3 */}
                <ColView
                  className={'d-flex flex-column mt-2'}
                  colSpec={6}
                  labelClassName={'text-muted'}
                  label={'Location'}
                  value={gamelocations}
                />

                {!isTilo && (
                  <ColView
                    className={'d-flex flex-column mt-2'}
                    labelClassName={'text-muted'}
                    label={'Pax'}
                    value={eventActivity?.approxPax}
                  />
                )}

                <ColView
                  className={'d-flex flex-column mt-2'}
                  colSpec={6}
                  labelClassName={'text-muted'}
                  label={'Language'}
                  value={eventActivity?.language}
                />

                {/* line 4 */}
                <ColView
                  colSpec={12}
                  className={'d-flex flex-column mt-2'}
                  labelClassName={'text-muted'}
                  label={'Primary Contact'}
                  value={facilitatorData}
                />
              </div>
            </Col>
          </Row>
        )}

        {/* TILO section */}
        {isTilo && (
          <>
            <Row className={'p-2'}>
              <Col md={10}>
                <div className={'d-flex flex-wrap'}>
                  <ColView
                    className={'d-flex flex-column fw-bold'}
                    colSpec={12}
                    labelClassName={'text-muted'}
                    label={'Game'}
                    value={'TILO — The Infinite Loop'}
                  />
                  <ColView
                    className={'d-flex flex-column mt-2'}
                    colSpec={3}
                    labelClassName={'text-muted'}
                    label={'Session Type'}
                    value={eventActivity?.tiloGameType}
                  />
                  <ColView
                    className={'d-flex flex-column mt-2'}
                    colSpec={3}
                    labelClassName={'text-muted'}
                    label={'Doors'}
                    value={eventActivity?.tiloNbrDoors?.toString()}
                  />
                  <ColView
                    className={'d-flex flex-column mt-2'}
                    colSpec={3}
                    labelClassName={'text-muted'}
                    label={'Teams'}
                    value={eventActivity?.tiloTeamsCount?.toString()}
                  />
                  <ColView
                    className={'d-flex flex-column mt-2'}
                    colSpec={3}
                    labelClassName={'text-muted'}
                    label={'Players / Team'}
                    value={eventActivity?.tiloNbrPlayersPerTeam?.toString()}
                  />
                  <ColView
                    className={'d-flex flex-column mt-2'}
                    colSpec={3}
                    labelClassName={'text-muted'}
                    label={'Duration (min)'}
                    value={eventActivity?.tiloPlaytime?.toString()}
                  />
                </div>
              </Col>
            </Row>

            {displayUrl && eventActivity?.tiloGameId && (
              <>
                <Row className={'p-2'}>
                  <Col md={'12'} className={'d-flex flex-column'}>
                    <small className={'text-muted'}>
                      TILO - Facilitator Link + QR Code
                    </small>
                    {eventActivity?.environmentHostUrl && (
                      <QRLink
                        title={'TILO - Facilitator Link + QR Code'}
                        activityName={'TILO'}
                        url={`${eventActivity.environmentHostUrl}/tilo/facilitator/${eventActivity.tiloGameId}?playlistId=${eventActivity.id}`}
                      />
                    )}
                  </Col>
                </Row>

                <Row className={'p-2'}>
                  <Col md={'12'} className={'d-flex flex-column'}>
                    <small className={'text-muted'}>
                      TILO - Participant Link + QR Code
                    </small>
                    {tiloWebGlUrl && (
                      <QRLink
                        title={'TILO - Participant Link + QR Code'}
                        activityName={'TILO'}
                        url={`${tiloWebGlUrl}/#/game/${eventActivity.tiloGameId}`}
                      />
                    )}
                  </Col>
                </Row>
              </>
            )}
          </>
        )}

        {/* TIL section */}
        {isTil && (
          <Row className={'p-2'}>
            <Col md={10}>
              <div className={'d-flex flex-wrap'}>
                <ColView
                  className={'d-flex flex-column fw-bold'}
                  colSpec={12}
                  labelClassName={'text-muted'}
                  label={'Game'}
                  value={'TIL — The Infinite Loop'}
                />
                <ColView
                  className={'d-flex flex-column mt-2'}
                  colSpec={3}
                  labelClassName={'text-muted'}
                  label={'Teams'}
                  value={eventActivity?.tiloTeamsCount?.toString()}
                />
                <ColView
                  className={'d-flex flex-column mt-2'}
                  colSpec={3}
                  labelClassName={'text-muted'}
                  label={'Duration (min)'}
                  value={eventActivity?.tiloPlaytime?.toString()}
                />
                <ColView
                  className={'d-flex flex-column mt-2'}
                  colSpec={3}
                  labelClassName={'text-muted'}
                  label={'Mode'}
                  value={
                    eventActivity?.tiloGameType === 'live' ? 'Live' : 'Test'
                  }
                />
                <ColView
                  className={'d-flex flex-column mt-2'}
                  colSpec={3}
                  labelClassName={'text-muted'}
                  label={'Collaborative'}
                  value={eventActivity?.tilIsCollab ? 'Yes' : 'No'}
                />
                <ColView
                  className={'d-flex flex-column mt-2'}
                  colSpec={6}
                  labelClassName={'text-muted'}
                  label={'Sync status'}
                  value={eventActivity?.tilIsSynced ? 'Synced' : 'Unsynced'}
                />
              </div>
            </Col>
          </Row>
        )}

        {/* Spark section */}
        {isSpark && (
          <>
            <Row className={'p-2'}>
              <Col md={10}>
                <div className={'d-flex flex-wrap'}>
                  <ColView
                    className={'d-flex flex-column fw-bold'}
                    colSpec={6}
                    labelClassName={'text-muted'}
                    label={'Game'}
                    value={
                      eventActivity?.game || eventActivity?.name || 'Spark Game'
                    }
                  />
                  <ColView
                    className={'d-flex flex-column'}
                    colSpec={6}
                    labelClassName={'text-muted'}
                    label={'Status'}
                    value={
                      eventActivity?.sparkGameStatus
                        ? eventActivity.sparkGameStatus.toUpperCase()
                        : 'DRAFT'
                    }
                  />
                  {(eventActivity?.sparkGameStatus?.toLowerCase() === 'ready' ||
                    eventActivity?.sparkGameStatus?.toLowerCase() ===
                      'locked') &&
                    eventActivity?.sparkJoinCode && (
                      <ColView
                        className={'d-flex flex-column mt-2'}
                        colSpec={6}
                        labelClassName={'text-muted'}
                        label={'Join Code'}
                        value={eventActivity.sparkJoinCode}
                      />
                    )}
                </div>
              </Col>
            </Row>

            {/* Spark View Guidance Box */}
            <Row className={'p-2'}>
              <Col md={'12'}>
                <div className="alert alert-warning small mb-0 py-2">
                  <b>You are currently viewing this event.</b> To configure your
                  Spark game, return to <b>My Events</b>, find this event and
                  click <b>Edit</b>
                  <span className="material-symbols-outlined ms-1 fs-3">
                    edit
                  </span>
                  . Then click the <b>Open Spark Editor</b> button to configure
                  your game.
                </div>
              </Col>
            </Row>

            {displayUrl && (
              <>
                {eventActivity?.sparkGameStatus?.toLowerCase() === 'ready' ||
                  (eventActivity?.sparkGameStatus?.toLowerCase() ===
                    'locked' && (
                    <>
                      {eventActivity?.sparkFacilitatorUrl && (
                        <Row className={'p-2'}>
                          <Col md={'12'} className={'d-flex flex-column'}>
                            <small className={'text-muted'}>
                              Spark - Facilitator Screen (Projector) + QR Code
                            </small>
                            <QRLink
                              title={'Spark - Facilitator Link'}
                              activityName={eventActivity?.game || 'Spark Game'}
                              url={eventActivity.sparkFacilitatorUrl}
                            />
                          </Col>
                        </Row>
                      )}

                      {eventActivity?.sparkJoinUrl && (
                        <Row className={'p-2'}>
                          <Col md={'12'} className={'d-flex flex-column'}>
                            <small className={'text-muted'}>
                              Spark - Participant Join Link + QR Code
                            </small>
                            <QRLink
                              title={`Spark - Player Join (Code: ${eventActivity?.sparkJoinCode ?? ''})`}
                              activityName={eventActivity?.game || 'Spark Game'}
                              url={eventActivity.sparkJoinUrl}
                            />
                          </Col>
                        </Row>
                      )}
                    </>
                  ))}
              </>
            )}
          </>
        )}

        {/* Standard game section */}
        {!isTilo && !isTil && !isSpark && eventActivity.game && (
          <>
            <Row className={'p-2'}>
              <Col md={10} className="d-flex">
                <ColView
                  className={'d-flex flex-column'}
                  colSpec={6}
                  labelClassName={'text-muted'}
                  label={'Game'}
                  value={eventActivity?.game}
                />
              </Col>
            </Row>

            {displayUrl && (
              <>
                {eventActivity.type !== 'BP' &&
                  eventActivity.type !== 'BPO' &&
                  eventActivity.type !== 'WST' && (
                    <>
                      <Row className={'p-2'}>
                        <Col md={'12'} className={'d-flex flex-column'}>
                          <small className={'text-muted'}>
                            Game - Facilitator Link + QR Code
                          </small>
                          {eventActivity?.environmentHostUrl &&
                            eventActivity?.environmentHostUrl !== '' && (
                              <QRLink
                                title={'Game - Facilitator Link + QR Code'}
                                activityName={eventActivity?.game}
                                url={`${eventActivity?.environmentHostUrl}/games/${eventActivity?.code}/${['AR', 'VR'].includes(eventActivity.type ?? '') ? 'leaderboard' : 'facilitator'}`}
                              />
                            )}
                        </Col>
                      </Row>
                    </>
                  )}

                {eventActivity.type === 'BPO' && (
                  <>
                    <Row className={'p-2'}>
                      <Col md={'12'} className={'d-flex flex-column'}>
                        <small className={'text-muted'}>
                          Facilitator Link + QR Code
                        </small>
                        {eventActivity?.environmentHostUrl &&
                          eventActivity?.environmentHostUrl !== '' && (
                            <QRLink
                              title={'Facilitator Link + QR Code'}
                              activityName={eventActivity?.game}
                              url={`https://bpo.catalystglobal.com/${eventActivity?.code}`}
                            />
                          )}
                      </Col>
                    </Row>
                  </>
                )}

                {eventActivity.type === 'Game' && (
                  <>
                    <Row className={'p-2'}>
                      <Col md={'12'} className={'d-flex flex-column'}>
                        <small className={'text-muted'}>
                          Game - Participant Link + QR Code
                        </small>
                        {eventActivity?.environmentHostUrl &&
                          eventActivity?.environmentHostUrl !== '' && (
                            <QRLink
                              title={'Game - Participant Link + QR Code'}
                              activityName={eventActivity?.game}
                              url={`${eventActivity?.environmentHostUrl}/games/${eventActivity?.code}`}
                            />
                          )}
                      </Col>
                    </Row>

                    <Row className={'p-2'}>
                      <Col md={'12'} className={'d-flex flex-column'}>
                        <small className={'text-muted'}>
                          Game - Pre Load Link + QR Code
                        </small>
                        {eventActivity?.environmentHostUrl &&
                          eventActivity?.environmentHostUrl !== '' && (
                            <QRLink
                              title={'Game - Pre Load Link + QR Code'}
                              activityName={eventActivity?.game}
                              url={`${eventActivity?.environmentHostUrl}/preload/${eventActivity?.code}`}
                            />
                          )}
                      </Col>
                    </Row>

                    <div className={'px-2 pb-2'}>
                      <QRTaskActivity playlistId={eventActivity.id} />
                    </div>
                  </>
                )}

                {(eventActivity.type === 'AR' ||
                  eventActivity.type === 'VR') && (
                  <>
                    <Row className={'p-2'}>
                      <Col md={'12'} className={'d-flex flex-column'}>
                        <small className={'text-muted'}>
                          AR/VR - Participant URL
                        </small>
                        {eventActivity?.environmentHostUrl &&
                          eventActivity?.environmentHostUrl !== '' && (
                            <QRLink
                              title={'AR/VR - Participant URL'}
                              activityName={eventActivity?.game}
                              url={`${eventActivity?.environmentHostUrl}/arpyramid/${eventActivity?.code}`}
                            />
                          )}
                      </Col>
                    </Row>
                  </>
                )}
              </>
            )}
          </>
        )}

        {eventActivity.assessmentName && (
          <>
            <Row className={'p-2'}>
              <Col md={10} className="d-flex">
                <ColView
                  className={'d-flex flex-column fw-bold'}
                  colSpec={6}
                  labelClassName={'text-muted'}
                  label={'Assessment'}
                  value={eventActivity?.assessmentName}
                />
              </Col>
            </Row>

            {displayUrl && (
              <>
                <Row className={'p-2'}>
                  <Col md={'12'} className={'d-flex flex-column'}>
                    <small className={'text-muted'}>
                      Assessment - Facilitator Link + QR Code
                    </small>
                    {eventActivity?.environmentHostUrl &&
                      eventActivity?.environmentHostUrl !== '' && (
                        <QRLink
                          title={'Assessment - Facilitator Link + QR Code'}
                          activityName={eventActivity?.assessmentName}
                          url={`${eventActivity?.environmentHostUrl}/facilitator/assessment/${eventActivity?.code}`}
                        />
                      )}
                  </Col>
                </Row>

                <Row className={'p-2'}>
                  <Col md={'12'} className={'d-flex flex-column'}>
                    <small className={'text-muted'}>
                      Assessment - Participant Link + QR Code
                    </small>
                    {eventActivity?.environmentHostUrl &&
                      eventActivity?.environmentHostUrl !== '' && (
                        <QRLink
                          title={'Assessment - Participant Link + QR Code'}
                          activityName={eventActivity?.assessmentName}
                          url={`${eventActivity?.environmentHostUrl}/assessments/${eventActivity?.code}`}
                        />
                      )}
                  </Col>
                </Row>
              </>
            )}
          </>
        )}

        <Row className={'p-2'}>
          <ColView
            className={'d-flex flex-column'}
            colSpec={12}
            labelClassName={'text-muted'}
            label={'Notes'}
            value={eventActivity?.notes}
          />
        </Row>
      </div>
    </>
  );
};
